import {
  takeLatest,
  put,
  call,
  select,
  take,
  takeEvery
} from 'redux-saga/effects';
import { push } from 'connected-react-router';
import {
  getTemplatesIfNeeded,
  getPaginatedTemplatesIfNeeded,
  getTagsIfNeeded,
  getTemplateIfNeeded,
  createTemplateFromCurrent,
  updateTemplateFromCurrent,
  deleteTemplatesByIdList,
  warnAndArchive,
  autoSaveTimerStart,
  getPrintFile
} from './sagas';
import {
  GET_PRINT_TEMPLATES,
  GET_PAGINATED_PRINT_TEMPLATES,
  GET_PRINT_TEMPLATE,
  CREATE_PRINT_TEMPLATE,
  CREATING_PRINT_TEMPLATE_SUCCEEDED,
  UPDATE_PRINT_TEMPLATE,
  UPDATING_PRINT_TEMPLATE_SUCCEEDED,
  DELETE_PRINT_TEMPLATES,
  getPrintTemplates,
  getPaginatedPrintTemplates,
  CREATING_PRINT_TEMPLATE_FAILED,
  UPDATING_PRINT_TEMPLATE_FAILED,
  GETTING_PRINT_TEMPLATE_FAILED,
  GETTING_PRINT_TEMPLATES_FAILED,
  DELETING_PRINT_TEMPLATES_FAILED,
  WARN_AND_ARCHIVE_PRINT_TEMPLATE,
  GETTING_ASSOCIATED_CAMPAIGNS_FOR_PRINT_TEMPLATE_FAILED,
  PRINT_AUTO_SAVE_TIMER_START,
  GET_PRINT_TAGS,
  GET_PRINT_FILE,
  SET_CURRENT_PRINT_TEMPLATE,
  GETTING_PRINT_TEMPLATE_SUCCEEDED
} from './actions';
import { actions as UserActions } from '../user';
import { actions as userMessaging } from '../userMessaging';
import { actions as SettingsActions } from '../settings';
import { sagas as SettingsSaga } from '../settings';

/* Helper Sagas */
function* forceGetAllTemplates() {
  yield put(getPaginatedPrintTemplates({ force: true }));
  const currentPrintTemplate = yield select(
    store => store.printTemplate.current
  );
  if(window.self !== window.top) {
    yield put(push(`/grapejsEditor/${currentPrintTemplate.id}`));
  } else {
    yield put(push(`/print-templates/design/${currentPrintTemplate.id}`));
  }
}
function* waitForUserReady() {
  const userReady = yield select(store => store.user.ready);
  if (!userReady) {
    yield take(UserActions.LOGIN_READY);
  }
}

function* HandleFailuresSaga() {
  yield takeLatest(
    [
      CREATING_PRINT_TEMPLATE_FAILED,
      UPDATING_PRINT_TEMPLATE_FAILED,
      GETTING_PRINT_TEMPLATE_FAILED,
      GETTING_PRINT_TEMPLATES_FAILED,
      DELETING_PRINT_TEMPLATES_FAILED,
      GETTING_ASSOCIATED_CAMPAIGNS_FOR_PRINT_TEMPLATE_FAILED
    ],
    function*(action) {
      yield put(
        userMessaging.setMessageForFailedAction({
          type: action.type,
          error: action.payload
        })
      );
    }
  );
}

/* APP FLOW - Templates */
function* PrintTemplateGetManySaga() {
  yield takeLatest(GET_PRINT_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplatesIfNeeded, action);
  });
}
function* PaginatedPrintTemplateGetManySaga() {
  yield takeLatest(GET_PAGINATED_PRINT_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getPaginatedTemplatesIfNeeded, action);
  });
}
function* TagGetSaga() {
  yield takeLatest(GET_PRINT_TAGS, function*(action) {
    yield call(getTagsIfNeeded, action);
  });
}
function* PrintTemplateGetSingleSaga() {
  yield takeLatest(GET_PRINT_TEMPLATE, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplateIfNeeded, action);
  });
}

// brutally refresh to template list on any modification - this *could* be made to act just on the in-memory reducer instead.
function* PrintTemplateCreateSaga() {
  yield takeLatest(CREATE_PRINT_TEMPLATE, createTemplateFromCurrent);
}
function* PrintTemplateCreateSuccessSaga() {
  yield takeLatest(CREATING_PRINT_TEMPLATE_SUCCEEDED, forceGetAllTemplates);
}
function* PrintTemplateUpdateSaga() {
  yield takeLatest(UPDATE_PRINT_TEMPLATE, updateTemplateFromCurrent);
}
function* PrintTemplateUpdateSuccessSaga() {
  yield takeLatest(UPDATING_PRINT_TEMPLATE_SUCCEEDED, forceGetAllTemplates);
}

function* WarnAndArchivePrintTemplate() {
  yield takeLatest(WARN_AND_ARCHIVE_PRINT_TEMPLATE, warnAndArchive);
}

function* PrintTemplateDeleteSaga() {
  yield takeLatest(DELETE_PRINT_TEMPLATES, function*(action) {
    yield call(deleteTemplatesByIdList, action);
    // yield call(forceGetAllTemplates); // force refresh even if the delete is unsuccessful - since we don't know if we only partially failed.
  });
}

function* AutoSaveTimerSaga() {
  yield takeEvery(PRINT_AUTO_SAVE_TIMER_START, function*(action) {
    yield call(autoSaveTimerStart, action);
  });
}

function* GetPrintFileSaga() {
  yield takeLatest(GET_PRINT_FILE, getPrintFile);
}

function* HandleTemplateLanguageChangeSaga() {
  yield call(SettingsSaga.waitForGetAllMetaData);
  const availableLanguages = yield select(store => store.settings.availableLanguages);
  yield takeLatest([
    SET_CURRENT_PRINT_TEMPLATE,
    GETTING_PRINT_TEMPLATE_SUCCEEDED
  ], function*(action) {
    let languageCode;
    if (action.type === SET_CURRENT_PRINT_TEMPLATE) {
      languageCode = action.payload?.languageCode;
    } else if (action.type === GETTING_PRINT_TEMPLATE_SUCCEEDED) {
      languageCode = action.payload?.template?.languageCode;
    }
    if (languageCode && availableLanguages.length > 0) {
      yield put(SettingsActions.updateTemplateLanguagePreference(availableLanguages.filter(language => language.languageCode === languageCode)[0]));
    }
  });
}

export default [
  PrintTemplateGetManySaga,
  PaginatedPrintTemplateGetManySaga,
  TagGetSaga,
  PrintTemplateGetSingleSaga,
  PrintTemplateCreateSaga,
  PrintTemplateCreateSuccessSaga,
  PrintTemplateUpdateSaga,
  PrintTemplateUpdateSuccessSaga,
  PrintTemplateDeleteSaga,
  HandleFailuresSaga,
  WarnAndArchivePrintTemplate,
  AutoSaveTimerSaga,
  GetPrintFileSaga,
  HandleTemplateLanguageChangeSaga
];
